import getDecorators from "inversify-inject-decorators";
import container from "../../../../ioc/inversify.config";
import TYPES from "../../../../ioc/types";
import { AuthService } from "../../../../services/auth-service";
import { SceneId } from "../../../../shared/enum";
import { drawBackground } from "../../../utils/scene-background";

const { lazyInject } = getDecorators(container);

export class LoginScene extends Phaser.Scene {
    @lazyInject(TYPES.AuthService)
    private authService: AuthService;

    private loginForm: HTMLFormElement;
    private errorText: Phaser.GameObjects.Text;
    private statusText: Phaser.GameObjects.Text;

    constructor() {
        super({ key: SceneId.LOGIN_SCREEN });
    }

    create() {
        drawBackground(this);

        const screenWidth = this.physics.world.bounds.width;
        const screenHeight = this.physics.world.bounds.height;

        // Title
        this.add
            .text(screenWidth / 2, 50, "Login", {
                fontSize: "48px",
                color: "#ffffff",
                fontFamily: "Arial, sans-serif",
                fontStyle: "bold",
            })
            .setOrigin(0.5);

        // Instructions
        const instructionText = this.add
            .text(screenWidth / 2, 110, "Enter your credentials to continue", {
                fontSize: "18px",
                color: "#aaaaaa",
                fontFamily: "Arial",
                resolution: 2,
            })
            .setOrigin(0.5);
        instructionText.setStroke("#000000", 2);

        // Status text
        this.statusText = this.add
            .text(screenWidth / 2, 140, "", {
                fontSize: "14px",
                color: "#00ff88",
                fontFamily: "Arial, sans-serif",
            })
            .setOrigin(0.5);

        // Error text
        this.errorText = this.add
            .text(screenWidth / 2, 420, "", {
                fontSize: "14px",
                color: "#ff6666",
                fontFamily: "Arial, sans-serif",
            })
            .setOrigin(0.5);

        // Create HTML form for input
        this.createLoginForm(screenWidth, screenHeight);

        // Add keyboard listener for ESC to switch to register
        this.input.keyboard.on("keydown-ESC", () => {
            this.showRegisterMode();
        });
    }

    private createLoginForm(screenWidth: number, screenHeight: number) {
        // Get the game canvas to position the form correctly
        const canvas = this.game.canvas;
        const canvasRect = canvas.getBoundingClientRect();

        // Create form container with CSS class
        this.loginForm = document.createElement("form");
        this.loginForm.className = "rpg-form";
        this.loginForm.id = "phaser-login-form";
        // Keep minimal positioning styles
        this.loginForm.style.position = "absolute";
        this.loginForm.style.left = `${canvasRect.left + (screenWidth * canvas.offsetWidth) / screenWidth / 2 - 150}px`;
        this.loginForm.style.top = `${canvasRect.top + 270}px`;
        this.loginForm.style.width = "300px";
        this.loginForm.style.zIndex = "1000";

        // Username input - styles from CSS
        const usernameLabel = document.createElement("label");
        usernameLabel.textContent = "Username:";

        const usernameInput = document.createElement("input");
        usernameInput.type = "text";
        usernameInput.id = "username";
        usernameInput.name = "username";
        usernameInput.required = true;

        // Password input - styles from CSS
        const passwordLabel = document.createElement("label");
        passwordLabel.textContent = "Password:";

        const passwordInput = document.createElement("input");
        passwordInput.type = "password";
        passwordInput.id = "password";
        passwordInput.name = "password";
        passwordInput.required = true;

        // Email input (for registration, hidden by default) - styles from CSS
        const emailLabel = document.createElement("label");
        emailLabel.textContent = "Email:";
        emailLabel.style.display = "none";
        emailLabel.id = "email-label";

        const emailInput = document.createElement("input");
        emailInput.type = "email";
        emailInput.id = "email";
        emailInput.name = "email";
        emailInput.style.display = "none";

        // Submit button - CSS classes
        const submitButton = document.createElement("button");
        submitButton.type = "submit";
        submitButton.textContent = "Login";
        submitButton.id = "submit-btn";
        submitButton.className = "rpg-button rpg-button-primary";

        // Toggle button - CSS classes
        const toggleButton = document.createElement("button");
        toggleButton.type = "button";
        toggleButton.textContent = "Register instead";
        toggleButton.id = "toggle-btn";
        toggleButton.className = "rpg-button rpg-button-secondary";
        toggleButton.style.marginTop = "10px";

        // Append elements
        this.loginForm.appendChild(usernameLabel);
        this.loginForm.appendChild(usernameInput);
        this.loginForm.appendChild(emailLabel);
        this.loginForm.appendChild(emailInput);
        this.loginForm.appendChild(passwordLabel);
        this.loginForm.appendChild(passwordInput);
        this.loginForm.appendChild(submitButton);
        this.loginForm.appendChild(toggleButton);

        document.body.appendChild(this.loginForm);

        // Form submit handler
        this.loginForm.addEventListener("submit", async (e) => {
            e.preventDefault();
            await this.handleSubmit(usernameInput.value, passwordInput.value, emailInput.value);
        });

        // Toggle button handler
        toggleButton.addEventListener("click", () => {
            const isLoginMode = submitButton.textContent === "Login";
            if (isLoginMode) {
                this.showRegisterMode();
            } else {
                this.showLoginMode();
            }
        });

        // Auto-focus on username
        usernameInput.focus();
    }

    private showRegisterMode() {
        const submitBtn = document.getElementById("submit-btn");
        const toggleBtn = document.getElementById("toggle-btn");
        const emailLabel = document.getElementById("email-label");
        const emailInput = document.getElementById("email") as HTMLInputElement;

        submitBtn.textContent = "Register";
        toggleBtn.textContent = "Login instead";
        emailLabel.style.display = "block";
        emailInput.style.display = "block";
        emailInput.required = true;
        this.errorText.setText("");
    }

    private showLoginMode() {
        const submitBtn = document.getElementById("submit-btn");
        const toggleBtn = document.getElementById("toggle-btn");
        const emailLabel = document.getElementById("email-label");
        const emailInput = document.getElementById("email") as HTMLInputElement;

        submitBtn.textContent = "Login";
        toggleBtn.textContent = "Register instead";
        emailLabel.style.display = "none";
        emailInput.style.display = "none";
        emailInput.required = false;
        this.errorText.setText("");
    }

    private async handleSubmit(username: string, password: string, email: string) {
        const isLoginMode =
            (document.getElementById("submit-btn") as HTMLButtonElement).textContent === "Login";

        this.errorText.setText("");
        this.statusText.setText(isLoginMode ? "Logging in..." : "Registering...");

        try {
            if (isLoginMode) {
                const userData = await this.authService.login(username, password);
                sessionStorage.setItem("user_id", userData.id);
                this.statusText.setText("Login successful!");
            } else {
                if (!email) {
                    this.errorText.setText("Email is required for registration");
                    this.statusText.setText("");
                    return;
                }
                const userData = await this.authService.register(username, email, password);
                sessionStorage.setItem("user_id", userData.id);
                this.statusText.setText("Registration successful!");
            }

            // Clean up the form
            this.cleanupForm();

            // Wait a bit to show success message
            this.time.delayedCall(500, () => {
                // Navigate to title screen
                this.scene.start(SceneId.TITLE_SCREEN);
            });
        } catch (error) {
            this.statusText.setText("");
            this.errorText.setText(error.message || "Authentication failed");
            console.error("Authentication error:", error);
        }
    }

    private cleanupForm() {
        if (this.loginForm && this.loginForm.parentNode) {
            this.loginForm.parentNode.removeChild(this.loginForm);
            this.loginForm = null;
        }
    }

    shutdown() {
        this.cleanupForm();
    }
}
